import React, { FC, useCallback, useEffect, useState } from "react";
import { toJS } from "mobx";
import { observer } from "mobx-react-lite";
import { Controller } from "react-hook-form";
import { debounce } from "lodash";
import { Row, Col, Tag } from "antd";
import {
  ExpandedTable,
  IButton,
  Layout,
  StoredPagination,
  SearchFormField,
  ConfirmModal,
  ISelect,
  ApprovalFormModal,
  DraggableModal,
  ITooltip,
} from "../../ui-kit";
import {
  ButtonEditIco,
  HistoryIco,
  PlusIco,
  TrashIco,
} from "../../assets/customIcons";
import DictionariesStore from "../../store/dictionary";
import CustomDictionaryForm from "./form/CustomDictionary";
import AttributesHistory from "./form/AttributesHistory";
import DictionaryVersion from "./form/DictionaryVersion";
import Paths from "../../constants/PathsBreadcrumbs";
import { useBreadcrumbs } from "../../hooks/useBreadcrumbs";
import { useHandleForm } from "../../hooks/useHandleForm";
import { useDeleteConfirm } from "../../hooks/useDeleteConfirm";
import { useStatusMessage } from "../../hooks/useStatusMessage";
import CustomDictionaryChildren from "./form/CustomDictionaryChidren";
import DictionaryGroupForm from "./form/DictionaryGroup";
import CustomDictionaryEditForm from "./form/CustomDictionaryEdit";
import DictionaryGroupEditForm from "./form/DictionaryGroupEdit";

import styles from "./styles.module.scss";
import Constants from "../../constants/Common";

const CustomDictionaries: FC = observer(() => {
  const {
    titleIcons: { trash, history: historyIcon, edit },
  } = Constants;
  const {
    dictData,
    paginator,
    search,
    sorter,
    filter,
    getDictList,
    isLoading,
    dictParamsRowKeys,
    dictParamsEditableRowKeys,
    isShowAddForm,
    isShowAddGroupForm,
  } = DictionariesStore;

  useEffect(() => {
    getDictList();
  }, [
    paginator.page,
    paginator.perPage,
    search.search.text,
    sorter.changingKey,
    filter.filterItems,
  ]);

  const [showHistoryModal, setShowHistoryModal] = useState({
    show: false,
    id: null,
    title: "",
  });
  const handleShowHistoryModal = () =>
    setShowHistoryModal((data) => ({ ...data, show: !data.show }));

  const onShowHistory = (e, { dictId, title }) => {
    e.stopPropagation();

    setShowHistoryModal({ show: true, id: dictId, title });
  };

  const [approvalDictId, setApprovalDictId] = useState(null);

  useEffect(() => {
    if (!DictionariesStore.dictFormIsSending) {
    }
  }, [DictionariesStore.dictFormIsSending]);

  const deleteData = (rec) => {
    if (rec.isGroup) DictionariesStore.deleteGroup(rec.groupId);
    else DictionariesStore.deleteDict(rec.dictId);
  };

  useStatusMessage(DictionariesStore.dictStateStatus);
  useBreadcrumbs(Paths.nsiСustom);

  const {
    confirmModalData,
    toggleConfirmModal,
    closeConfirmModal,
    confirmDelete,
  } = useDeleteConfirm(deleteData);

  const onDeleteRow = (e, rec) => {
    e.stopPropagation();
    toggleConfirmModal({ show: true, id: rec });
  };

  const columns = [
    {
      title: "Наименование",
      dataIndex: "itemTitle",
      key: "itemTitle",
      sortable: true,
      render: (text, record, index) =>
        record.isGroup ? record.groupTitle : record.title,
      width: "40%",
      className: "align-left",
    },

    {
      title: "Тип",
      dataIndex: "isGroup",
      key: "isGroup",
      render: (text, record, index) =>
        record.isGroup ? "Группа" : "Справочник",
      sortable: true,
      width: "5%",
      className: "align-left",
    },
    {
      title: "Описание",
      dataIndex: "description",
      key: "description",
      width: "40%",
      className: "align-left",
    },
    {
      title: "Статус согласования",
      key: "status",
      render: (_, record) => {
        const params = {
          color: "blue",
          children: record?.agreementStatus?.entityStateName,
          onClick: (e) => {
            e.stopPropagation();
            setApprovalDictId(record.dictId);
          },
        };
        if (record?.agreementStatus?.entityState == 3) {
          params.color = "red";
        }
        if (record?.agreementStatus?.entityState == 2) {
          params.color = "green";
        }
        if (record?.agreementStatus?.entityState == 1) {
          params.color = "yellow";
        }
        return record.isGroup ? <></> : <Tag {...params}></Tag>;
      },
    },
    {
      title: "Статус",
      key: "status",
      render: (_, record) => {
        const params = {
          color: "green",
          children: "Активен",
        };
        if (record?.isBlocked) {
          params.color = "yellow";
          params.children = "Заблокирован";
        }
        return record?.isGroup ? <></> : <Tag {...params}></Tag>;
      },
    },
    {
      title: "Количество элементов",
      key: "itemsCount",
      width: "5%",
      className: "align-left",
      render: (text, record, index) =>
        !record.isGroup
          ? `${record.itemsCount}${
              record.itemsFilteredCount != record.itemsCount
                ? "(" + record.itemsFilteredCount + ")"
                : ""
            }`
          : "",
    },
    {
      title: " ",
      key: "edit",
      render: (text, record, index) => {
        const onClick = (e) => {
          let keys = toJS(dictParamsEditableRowKeys.keys);
          const isExpanded = keys.includes(record.id);
          if (!isExpanded && dictParamsRowKeys.keys.includes(record.id)) {
            e.stopPropagation();
          }
          if (isExpanded) {
            keys = keys.filter((item) => item !== record.id);
          } else {
            keys.push(record.id);
          }
          dictParamsEditableRowKeys.setKeys(keys);
        };
        return (
          <ITooltip title={edit} placement="right">
            <ButtonEditIco onClick={onClick} />
          </ITooltip>
        );
      },
      className: "delete-cell",
    },
    // {
    //   title: " ",
    //   key: "history",
    //   fixed: "right",
    //   render: (text, record, index) =>
    //     !record.isGroup ? (
    //       <ITooltip title={historyIcon} placement="right">
    //         <HistoryIco onClick={(e) => onShowHistory(e, record)} />
    //       </ITooltip>
    //     ) : null,
    //   className: "delete-cell",
    // },
    {
      title: " ",
      key: "delete",
      render: (text, record, index) => (
        <ITooltip title={trash} placement="right">
          <TrashIco onClick={(e) => onDeleteRow(e, record)} />
        </ITooltip>
      ),
      className: "delete-cell",
    },
  ];

  const {
    control,
    watchAllField,
    setValue,
    isFocused,
    handleBlur,
    handleFocus,
  } = useHandleForm(null, { search: "" }, { search: false });

  const debouncedQuery = useCallback(
    debounce((query) => search.setText(query), 500),
    [search],
  );

  const onFindClick = () => {
    search.setText(watchAllField.search);
  };

  const handleChangeSearch = (event) => {
    setValue("search", event.target.value);
    if (event.target.value) {
      debouncedQuery(event.target.value);
    }
  };

  const handleClearSearch = () => {
    setValue("search", "");
    search.setText("");
  };

  const handleStatusSelect = (v) => {
    if (v === "Заблокирован") {
      filter.setFilterItem("isBlocked", true);
    } else if (v === "Активен") {
      filter.setFilterItem("isBlocked", false);
    } else {
      filter.setFilterItem("isBlocked", "");
    }

    getDictList();
  };

  return (
    <Layout type="white">
      <div className={styles.Filter}>
        <div className={styles.controls}>
          <Controller
            control={control}
            name="isDeleted"
            render={({ field }) => (
              <ISelect
                field={field}
                placeholder="Статус"
                onChange={handleStatusSelect}
                className={styles.statusFilter}
                optionsArr={[
                  {
                    id: "Заблокирован",
                    name: "Заблокирован",
                  },
                  {
                    id: "Активен",
                    name: "Активен",
                  },
                ]}
              />
            )}
          />

          <SearchFormField
            name="searchText"
            label="Поиск (Название группы справочника, группы, элемента)"
            type="text"
            className={styles.search}
            value={watchAllField.search}
            isFocused={isFocused.searchText}
            onClear={handleClearSearch}
            onChange={handleChangeSearch}
            onFindClick={onFindClick}
          />
        </div>
      </div>
      <div className={styles.UsersPage}>
        <Row gutter={[10, 10]}>
          <Col span={24}>
            <ExpandedTable
              key={dictParamsEditableRowKeys.keys.join("")}
              loading={isLoading.value}
              rowKeys={dictParamsRowKeys.keys}
              setRowKeys={dictParamsRowKeys.setKeys}
              columns={columns}
              sort={sorter}
              data={dictData}
              className={styles.UsersTable}
              expandedRowRender={(record) => {
                if (
                  record.isGroup &&
                  dictParamsEditableRowKeys.keys.includes(record.id)
                ) {
                  return (
                    <>
                      <a
                        onClick={(e) =>
                          dictParamsEditableRowKeys.remove(record.id)
                        }
                      >
                        Вернуться к списку
                      </a>
                      {React.cloneElement(<DictionaryGroupEditForm />, {
                        record,
                      })}
                    </>
                  );
                }
                if (
                  !record.isGroup &&
                  dictParamsEditableRowKeys.keys.includes(record.id)
                ) {
                  return (
                    <>
                      <a
                        onClick={(e) =>
                          dictParamsEditableRowKeys.remove(record.id)
                        }
                      >
                        Вернуться к списку
                      </a>
                      {React.cloneElement(<CustomDictionaryEditForm />, {
                        record,
                      })}
                    </>
                  );
                }
                return React.cloneElement(<CustomDictionaryChildren />, {
                  record,
                });
              }}
              //rowExpandable={(record) => record?.isGroup}
            />
          </Col>
          <Col span={24}>
            <StoredPagination {...paginator} />
          </Col>

          <Col>
            <IButton size="medium" onClick={(e) => isShowAddForm.toggle()}>
              <PlusIco /> Добавить справочник
            </IButton>
          </Col>
          <Col>
            <IButton
              size="medium"
              onClick={(e) => isShowAddGroupForm.toggle()}
              className={styles.customAddGroupButton}
            >
              <PlusIco /> Добавить группу
            </IButton>
          </Col>
          {isShowAddGroupForm.value && (
            <Col span={24}>
              <DictionaryGroupForm />
            </Col>
          )}
          {isShowAddForm.value && (
            <Col span={24}>
              <CustomDictionaryForm />
            </Col>
          )}
        </Row>
      </div>
      <ConfirmModal
        message={`Вы действительно хотите удалить ${
          confirmModalData.id?.isGroup ? "группу" : "справочник"
        } "${
          confirmModalData.id?.isGroup
            ? confirmModalData.id?.groupTitle
            : confirmModalData.id?.title
        }"?`}
        visible={confirmModalData.show}
        onClose={closeConfirmModal}
        onConfirm={confirmDelete}
      />
      {approvalDictId && (
        <ApprovalFormModal
          entityId={approvalDictId}
          entityName="Справочники"
          onClose={() => setApprovalDictId(null)}
        />
      )}
      <DraggableModal
        visible={showHistoryModal.show}
        title={`История изменений`}
        footer={null}
        onCancel={handleShowHistoryModal}
      >
        <AttributesHistory dictionaryId={showHistoryModal.id} />
        <DictionaryVersion
          dictionaryId={showHistoryModal.id}
          dictionaryName={showHistoryModal.title}
        />
      </DraggableModal>
    </Layout>
  );
});

export default CustomDictionaries;
